import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Copy, RefreshCw, Smartphone, Loader2 } from "lucide-react";
import { API } from "@/config";

export default function CalendarPanel({ headers }) {
  const [cal, setCal] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/admin/calendar`, { headers })
      .then((res) => setCal(res.data))
      .catch(() => toast.error("No se pudo cargar el enlace del calendario"));
  }, []);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(cal.url);
      toast.success("Enlace copiado");
    } catch {
      toast.error("No se pudo copiar, selecciona el texto manualmente");
    }
  };

  const regenerate = async () => {
    if (!window.confirm("Se creará un enlace nuevo y el anterior dejará de funcionar. ¿Continuar?")) return;
    setBusy(true);
    try {
      const res = await axios.post(`${API}/admin/calendar/regenerate`, {}, { headers });
      setCal(res.data);
      toast.success("Enlace nuevo generado. Vuelve a suscribirte en tu iPhone");
    } catch {
      toast.error("No se pudo regenerar el enlace");
    } finally {
      setBusy(false);
    }
  };

  if (!cal) return <div className="mt-6 h-48 rounded-2xl bg-sand/50 animate-pulse" />;

  return (
    <section data-testid="admin-calendar-panel">
      <h1 className="font-display text-2xl sm:text-3xl font-semibold text-obsidian">Mi agenda en el iPhone</h1>
      <p className="text-sm text-taupe mt-1">
        Suscribe tu iPhone a este calendario y verás todas tus citas (clienta, servicio y hora) en la app Calendario. Se actualiza solo.
      </p>

      <div className="mt-6 rounded-3xl bg-creamcard border border-sand p-6 sm:p-8 grid gap-8">
        <div className="grid gap-3">
          <p className="text-xs tracking-[0.25em] uppercase text-gold font-medium">Opción rápida (desde el iPhone)</p>
          <a
            href={cal.webcal}
            data-testid="calendar-webcal-link"
            className="inline-flex w-fit items-center gap-2 rounded-xl bg-gold hover:bg-gold-dark text-white text-sm font-medium px-6 py-3 transition-colors duration-200"
          >
            <Smartphone className="w-4 h-4" /> Añadir a mi calendario
          </a>
          <p className="text-xs text-taupe">Abre este panel en Safari de tu iPhone, pulsa el botón y acepta "Suscribirse".</p>
        </div>

        <div className="grid gap-3">
          <p className="text-xs tracking-[0.25em] uppercase text-gold font-medium">Opción manual</p>
          <ol className="text-sm text-cocoa list-decimal pl-5 space-y-1">
            <li>En el iPhone: <b>Ajustes → Apps → Calendario → Cuentas → Añadir cuenta → Otro → Añadir calendario suscrito</b>.</li>
            <li>Pega este enlace y pulsa Siguiente → Guardar.</li>
          </ol>
          <div className="flex flex-col sm:flex-row gap-3">
            <input
              readOnly
              value={cal.url}
              data-testid="calendar-url-input"
              onFocus={(e) => e.target.select()}
              className="w-full rounded-xl border border-sand bg-cream px-4 py-2.5 text-xs text-obsidian focus:outline-none focus:ring-2 focus:ring-gold"
            />
            <button
              onClick={copy}
              data-testid="calendar-copy-btn"
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-gold text-gold hover:bg-gold hover:text-white text-sm font-medium px-5 py-2.5 transition-colors duration-200"
            >
              <Copy className="w-4 h-4" /> Copiar
            </button>
          </div>
          <p className="text-xs text-taupe">También sirve en Google Calendar (Otros calendarios → Desde URL). El iPhone actualiza los calendarios suscritos cada poco tiempo, no al instante.</p>
        </div>

        <div className="grid gap-2">
          <p className="text-xs tracking-[0.25em] uppercase text-gold font-medium">Privacidad</p>
          <p className="text-sm text-cocoa">Este enlace es secreto: quien lo tenga puede ver tus citas. Si lo compartes por error, genera uno nuevo.</p>
          <button
            onClick={regenerate}
            disabled={busy}
            data-testid="calendar-regenerate-btn"
            className="inline-flex w-fit items-center gap-2 rounded-xl border border-sand text-taupe hover:text-obsidian text-sm px-5 py-2.5 transition-colors duration-200"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />} Generar enlace nuevo
          </button>
        </div>
      </div>
    </section>
  );
}
